/**
 * SubscriptionGuard — defensa en capa de ruta para garantizar que nadie
 * entre al panel cliente sin un plan asignado (pago o gratis).
 *
 * Regla de negocio invariante (beta Y producción):
 *   - Todo user con role primario 'user' (cliente) DEBE tener una fila en
 *     `subscriptions` con status ∈ {active, trialing, past_due} para acceder
 *     a /dashboard/*.
 *   - Si no la tiene → forzamos /pricing para que elija plan.
 *   - Durante beta, /pricing solo ofrece Pro/Premium (Free está desactivado).
 *     Post-beta, /pricing va a ofrecer Free + Pro + Premium.
 *   - En cualquier caso, el user no puede esquivar este paso entrando por
 *     URL directa, link de referido, OAuth, ni nada.
 *
 * Excepciones (NO requieren sub):
 *   - admin → puede ver todo sin sub (uso interno / QA)
 *   - guru  → ya está bloqueado por ClientGuard hacia /guru. Si por algún
 *             motivo cae acá, tampoco requiere sub (no es cliente).
 *
 * Orden recomendado de wrappers:
 *   AuthGuard > ClientGuard > SubscriptionGuard > DashboardLayout
 *   (AuthGuard exige sesión, ClientGuard saca a los guru, este saca a los
 *   user sin sub. Cuando llegas a DashboardLayout, eres cliente con plan.)
 *
 * Por qué un guard a nivel ruta y no solo un redirect en signup:
 *   El RegisterPage tiene control sobre el redirect inmediato post-signup,
 *   pero NO sobre los signups que se hacen via OAuth (vuelven por callback),
 *   email confirmation tardía, returns de sesión existente que en algún
 *   momento perdieron la sub, o cualquier tipo de bypass por URL directa.
 *   Este guard cubre todo eso: cada navegación a /dashboard/* se valida
 *   contra la DB en tiempo real, sin depender del flujo de entrada.
 */
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '@/lib/auth';
import { useUserRoles, type PrimaryRole } from '@/lib/hooks/useUserRoles';
import { useMySubscription } from '@/lib/api/subscription';
import { useFreePlanOpen } from '@/lib/hooks/useBetaActive';
import { ProcessingSplash } from '@/components/ui/processing-splash';

// =====================================================================
// Diseño del "esperando webhook" — durable a través de URL stripping.
// =====================================================================
// Antes usábamos ?welcome=1 como señal, pero:
//   - DashboardHome borra ese query param apenas se monta (Sprint 7)
//   - Cualquier ancestro que llame setSearchParams puede pisarlo
//   - Un refresh lo pierde
// Solución: cuando detectamos welcome=1 EN LA URL, lo migramos a
// localStorage con un timestamp. El flag vive hasta que la sub aparezca
// (lo limpiamos) o el timeout se vence. Así es durable.
const WAIT_FLAG_KEY = 'gurumba.waiting_for_subscription_until';
const POST_CHECKOUT_WAIT_MS = 60_000; // 60s — webhooks lentos sí pasan

function readWaitDeadline(): number | null {
  try {
    const raw = localStorage.getItem(WAIT_FLAG_KEY);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}
function writeWaitDeadline(deadline: number) {
  try { localStorage.setItem(WAIT_FLAG_KEY, String(deadline)); } catch {
    // localStorage no disponible (Safari incógnito, quota llena, cookies off).
    // El flujo degrada: el guard usará el estado de la query indirecta.
  }
}
function clearWaitDeadline() {
  try { localStorage.removeItem(WAIT_FLAG_KEY); } catch {
    // Mismo motivo: si localStorage no está disponible, no hay nada que limpiar.
  }
}

export function SubscriptionGuard({ children }: { children: React.ReactNode }) {
  const user = useUser();
  const { data: roles, isFetching: rolesFetching, refetch: refetchRoles } = useUserRoles();
  const role: PrimaryRole | null = !roles
    ? null
    : roles.includes('admin')
      ? 'admin'
      : roles.includes('guru')
        ? 'guru'
        : 'user';
  const location = useLocation();
  const qc = useQueryClient();
  // Freemium: con la beta cerrada el cliente sin plan es Free y entra a su
  // espacio (lo de miembros se ve con candado adentro de cada pantalla).
  const { isFreeOpen, isLoading: freeLoading } = useFreePlanOpen();

  // Detectamos ?welcome=1 una sola vez. Si está, escribimos el deadline
  // a localStorage. Después la URL puede cambiar lo que quiera — ya
  // estamos en "modo espera".
  useEffect(() => {
    if (new URLSearchParams(location.search).get('welcome') === '1') {
      const existing = readWaitDeadline();
      // Solo escribimos si no había uno activo y vigente — para no extender
      // accidentalmente la espera si el user re-entra a /dashboard?welcome=1.
      if (!existing || existing < Date.now()) {
        writeWaitDeadline(Date.now() + POST_CHECKOUT_WAIT_MS);
      }
      // Forzamos refetch inmediato — la query puede tener un null cacheado
      // de antes del checkout y queremos romper esa cache YA.
      qc.invalidateQueries({ queryKey: ['my_subscription'] });
    }
  }, [location.search, qc]);

  // ¿Estamos dentro de la ventana de espera? Lo recomputamos en cada render.
  // Si el deadline expiró, lo limpiamos y mandamos a pricing.
  const deadline = readWaitDeadline();
  const now = Date.now();
  const isWaiting = deadline !== null && deadline > now;

  const { data: subscription, isLoading: subLoading } = useMySubscription({
    pollWhileMissing: isWaiting,
  });

  // Cuando aparece la sub, limpiamos el flag para que un refresh no
  // vuelva a entrar en modo espera.
  useEffect(() => {
    if (subscription) clearWaitDeadline();
  }, [subscription]);

  // Si el deadline expiró sin sub, limpiamos para no quedar pegados.
  useEffect(() => {
    if (deadline !== null && deadline <= now && !subscription) {
      clearWaitDeadline();
    }
  }, [deadline, now, subscription]);

  // Trigger de re-render cuando el deadline pasa, para no esperar a la
  // próxima refetch. Programamos un timer hasta el deadline.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!isWaiting) return;
    const ms = Math.max(500, deadline! - Date.now());
    const t = setTimeout(() => forceTick((x) => x + 1), ms);
    return () => clearTimeout(t);
  }, [isWaiting, deadline]);

  // Anti-rebote de rol: un 'user' que iría a /pricing puede ser en realidad un
  // guru/admin con la cache de roles vieja (ej. recién promovido a guru). Antes
  // de rebotar, refrescamos los roles UNA vez y esperamos el resultado real.
  const [rolesRechecked, setRolesRechecked] = useState(false);
  useEffect(() => {
    if (role === 'user' && !subscription && !isWaiting && !rolesRechecked) {
      void refetchRoles().finally(() => setRolesRechecked(true));
    }
  }, [role, subscription, isWaiting, rolesRechecked, refetchRoles]);

  // 1) Sin sesión → lo maneja AuthGuard antes; defensivo igual
  if (!user) return <Navigate to="/login" replace />;

  // 2) Roles/sub aún cargando.
  if (role === null || subLoading) {
    // Si estamos en modo espera post-checkout, splash branded desde el
    // primer instante — no tiene sentido un spinner pelado seguido del splash.
    if (isWaiting) return <ProcessingSplash />;
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-accent border-t-transparent" />
      </div>
    );
  }

  // 3) admin / guru → bypass. No son clientes, no tienen sub.
  if (role === 'admin' || role === 'guru') {
    return <>{children}</>;
  }

  // 4) Cliente sin sub activa.
  if (!subscription) {
    // 4a) Estamos dentro de la ventana de espera del webhook → splash.
    //     `pollWhileMissing` está refetcheando cada 2s en background.
    if (isWaiting) return <ProcessingSplash />;

    // 4b) Anti-rebote: antes de mandar a /pricing, confirmamos el rol con una
    //     recarga fresca (evita rebotar a un guru/admin con cache vieja). Si
    //     todavía se está reconfirmando, spinner en vez de rebote.
    if (!rolesRechecked || rolesFetching || freeLoading) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-accent border-t-transparent" />
        </div>
      );
    }

    // 4c) Plan Free abierto (beta cerrada): pasa como Free.
    if (isFreeOpen) return <>{children}</>;

    // 4d) Beta abierta: cliente sin plan → upgrade ADENTRO del panel
    //     (Mi cuenta → Suscripción), no al /pricing del sitio público.
    //     El cliente de sesión suelta es un usuario legítimo: se le ofrece
    //     la membresía sin escupirlo del dashboard.
    return <Navigate to="/account?tab=suscripcion" replace />;
  }

  // 5) Tiene sub activa/trialing/past_due → pasa.
  return <>{children}</>;
}
